import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useServerStore } from '../../store/serverStore';
import { X, Plus, Edit, Trash2, Save } from 'lucide-react';

interface RoleManagementModalProps {
  serverId: string;
  onClose: () => void;
}

interface Role {
  id: string;
  name: string;
  color: string;
  permissions: {
    [key: string]: boolean;
  };
}

const RoleManagementModal: React.FC<RoleManagementModalProps> = ({ serverId, onClose }) => {
  const [roles, setRoles] = useState<Role[]>([
    {
      id: '1',
      name: 'Owner',
      color: '#FFD700',
      permissions: {
        manageServer: true,
        manageRoles: true,
        manageChannels: true,
        kickMembers: true,
        banMembers: true,
        manageMessages: true,
        sendMessages: true,
        readMessages: true,
        useVoice: true
      }
    },
    {
      id: '2',
      name: 'Admin',
      color: '#FF0000',
      permissions: {
        manageServer: false,
        manageRoles: true,
        manageChannels: true,
        kickMembers: true,
        banMembers: true,
        manageMessages: true,
        sendMessages: true,
        readMessages: true,
        useVoice: true
      }
    },
    {
      id: '3',
      name: 'Moderator',
      color: '#0000FF',
      permissions: {
        manageServer: false,
        manageRoles: false,
        manageChannels: false,
        kickMembers: true,
        banMembers: true,
        manageMessages: true,
        sendMessages: true,
        readMessages: true,
        useVoice: true
      }
    },
    {
      id: '4',
      name: 'Member',
      color: '#808080',
      permissions: {
        manageServer: false,
        manageRoles: false,
        manageChannels: false,
        kickMembers: false,
        banMembers: false,
        manageMessages: false,
        sendMessages: true,
        readMessages: true,
        useVoice: true
      }
    }
  ]);
  
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [newRole, setNewRole] = useState<Role>({
    id: '',
    name: '',
    color: '#f0f0f0',
    permissions: {
      manageServer: false,
      manageRoles: false,
      manageChannels: false,
      kickMembers: false,
      banMembers: false,
      manageMessages: false,
      sendMessages: true,
      readMessages: true,
      useVoice: true
    }
  });
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleCreateRole = () => {
    setIsCreating(true);
    setEditingRole(null);
  };

  const handleEditRole = (role: Role) => {
    setEditingRole({ ...role });
    setIsCreating(false);
  };

  const handleDeleteRole = (roleId: string) => {
    if (roleId === '1' || roleId === '4') {
      setError('Default roles cannot be deleted');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this role?')) {
      setRoles(roles.filter(role => role.id !== roleId));
      setSuccess('Role deleted successfully');
    }
  };

  const handleSaveNewRole = () => {
    if (!newRole.name.trim()) {
      setError('Role name is required');
      return;
    }
    
    const newRoleWithId = {
      ...newRole,
      id: Date.now().toString()
    };
    
    setRoles([...roles, newRoleWithId]);
    setNewRole({
      id: '',
      name: '',
      color: '#f0f0f0',
      permissions: {
        manageServer: false,
        manageRoles: false,
        manageChannels: false,
        kickMembers: false,
        banMembers: false,
        manageMessages: false,
        sendMessages: true,
        readMessages: true,
        useVoice: true
      }
    });
    setIsCreating(false);
    setSuccess('Role created successfully');
  };

  const handleSaveEditedRole = () => {
    if (!editingRole) return;
    
    if (!editingRole.name.trim()) {
      setError('Role name is required');
      return;
    }
    
    setRoles(roles.map(role => role.id === editingRole.id ? editingRole : role));
    setEditingRole(null);
    setSuccess('Role updated successfully');
  };

  const handleCancelEdit = () => {
    setEditingRole(null);
    setIsCreating(false);
  };

  const handlePermissionChange = (permission: string, value: boolean, isForNewRole: boolean) => {
    if (isForNewRole) {
      setNewRole({
        ...newRole,
        permissions: {
          ...newRole.permissions,
          [permission]: value
        }
      });
    } else if (editingRole) {
      setEditingRole({
        ...editingRole,
        permissions: {
          ...editingRole.permissions,
          [permission]: value
        }
      });
    }
  };

  const renderPermissionCheckbox = (
    label: string, 
    permission: string, 
    role: Role, 
    isForNewRole: boolean,
    disabled: boolean = false
  ) => (
    <div className="flex items-center">
      <input
        id={`${isForNewRole ? 'new' : role.id}-${permission}`}
        type="checkbox"
        checked={isForNewRole ? newRole.permissions[permission] : role.permissions[permission]}
        onChange={(e) => handlePermissionChange(permission, e.target.checked, isForNewRole)}
        disabled={disabled}
        className="h-4 w-4 text-accent-color focus:ring-accent-color border-border-color rounded bg-bg-tertiary disabled:opacity-50"
      />
      <label 
        htmlFor={`${isForNewRole ? 'new' : role.id}-${permission}`} 
        className={`ml-2 text-sm ${disabled ? 'text-text-muted' : 'text-text-secondary'}`}
      >
        {label}
      </label>
    </div>
  );

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[200]">
      <div className="bg-bg-secondary rounded-lg w-full max-w-4xl h-[80vh] overflow-y-auto animate-fade-in">
        <div className="flex justify-between items-center p-4 border-b border-border-color sticky top-0 bg-bg-secondary z-10">
          <h2 className="text-xl font-bold text-text-primary">Role Management</h2>
          <button 
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-6">
          {error && (
            <div className="bg-error/20 border border-error text-error p-3 rounded-md mb-4 text-sm">
              {error}
            </div>
          )}
          
          {success && (
            <div className="bg-success/20 border border-success text-success p-3 rounded-md mb-4 text-sm">
              {success}
            </div>
          )}
          
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold text-text-primary">Server Roles</h3>
            <button
              onClick={handleCreateRole}
              className="btn btn-primary flex items-center"
            >
              <Plus className="h-4 w-4 mr-1" />
              Create Role
            </button>
          </div>
          
          {isCreating && (
            <div className="card mb-6">
              <h3 className="text-lg font-bold text-text-primary mb-4">Create New Role</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="new-role-name" className="block text-sm font-medium text-text-secondary mb-1">
                    Role Name
                  </label>
                  <input
                    id="new-role-name"
                    type="text"
                    value={newRole.name}
                    onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                    className="input w-full"
                    placeholder="Enter role name"
                  />
                </div>
                
                <div>
                  <label htmlFor="new-role-color" className="block text-sm font-medium text-text-secondary mb-1">
                    Role Color
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      id="new-role-color"
                      type="color"
                      value={newRole.color}
                      onChange={(e) => setNewRole({ ...newRole, color: e.target.value })}
                      className="h-10 w-10 bg-transparent border-0 p-0 rounded"
                    />
                    <input
                      type="text"
                      value={newRole.color}
                      onChange={(e) => setNewRole({ ...newRole, color: e.target.value })}
                      className="input w-full"
                      placeholder="Hex color code"
                    />
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2">Permissions</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {renderPermissionCheckbox('Manage Server', 'manageServer', newRole, true)}
                    {renderPermissionCheckbox('Manage Roles', 'manageRoles', newRole, true)}
                    {renderPermissionCheckbox('Manage Channels', 'manageChannels', newRole, true)}
                    {renderPermissionCheckbox('Kick Members', 'kickMembers', newRole, true)}
                    {renderPermissionCheckbox('Ban Members', 'banMembers', newRole, true)}
                    {renderPermissionCheckbox('Manage Messages', 'manageMessages', newRole, true)}
                    {renderPermissionCheckbox('Send Messages', 'sendMessages', newRole, true)}
                    {renderPermissionCheckbox('Read Messages', 'readMessages', newRole, true)}
                    {renderPermissionCheckbox('Use Voice', 'useVoice', newRole, true)}
                  </div>
                </div>
                
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={handleCancelEdit}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveNewRole}
                    className="btn btn-primary flex items-center"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Role
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {editingRole && (
            <div className="card mb-6">
              <h3 className="text-lg font-bold text-text-primary mb-4">Edit Role: {editingRole.name}</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="edit-role-name" className="block text-sm font-medium text-text-secondary mb-1">
                    Role Name
                  </label>
                  <input
                    id="edit-role-name"
                    type="text"
                    value={editingRole.name}
                    onChange={(e) => setEditingRole({ ...editingRole, name: e.target.value })}
                    disabled={editingRole.id === '1' || editingRole.id === '4'}
                    className="input w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Enter role name"
                  />
                </div>
                
                <div>
                  <label htmlFor="edit-role-color" className="block text-sm font-medium text-text-secondary mb-1">
                    Role Color
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      id="edit-role-color"
                      type="color"
                      value={editingRole.color}
                      onChange={(e) => setEditingRole({ ...editingRole, color: e.target.value })}
                      disabled={editingRole.id === '1' || editingRole.id === '4'}
                      className="h-10 w-10 bg-transparent border-0 p-0 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <input
                      type="text"
                      value={editingRole.color}
                      onChange={(e) => setEditingRole({ ...editingRole, color: e.target.value })}
                      disabled={editingRole.id === '1' || editingRole.id === '4'}
                      className="input w-full disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="Hex color code"
                    />
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2">Permissions</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {renderPermissionCheckbox('Manage Server', 'manageServer', editingRole, false, editingRole.id === '1')}
                    {renderPermissionCheckbox('Manage Roles', 'manageRoles', editingRole, false, editingRole.id === '1')}
                    {renderPermissionCheckbox('Manage Channels', 'manageChannels', editingRole, false, editingRole.id === '1')}
                    {renderPermissionCheckbox('Kick Members', 'kickMembers', editingRole, false, editingRole.id === '1')}
                    {renderPermissionCheckbox('Ban Members', 'banMembers', editingRole, false, editingRole.id === '1')}
                    {renderPermissionCheckbox('Manage Messages', 'manageMessages', editingRole, false, editingRole.id === '1')}
                    {renderPermissionCheckbox('Send Messages', 'sendMessages', editingRole, false)}
                    {renderPermissionCheckbox('Read Messages', 'readMessages', editingRole, false)}
                    {renderPermissionCheckbox('Use Voice', 'useVoice', editingRole, false)}
                  </div>
                </div>
                
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={handleCancelEdit}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEditedRole}
                    className="btn btn-primary flex items-center"
                    disabled={editingRole.id === '1' || editingRole.id === '4'}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-3">
            {roles.map((role) => (
              <div key={role.id} className="card flex items-center justify-between">
                <div className="flex items-center">
                  <div 
                    className="w-4 h-4 rounded-full mr-2" 
                    style={{ backgroundColor: role.color }}
                  ></div>
                  <span className="font-medium text-text-primary">{role.name}</span>
                  {(role.id === '1' || role.id === '4') && (
                    <span className="ml-2 text-xs text-text-muted">Default role</span>
                  )}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditRole(role)}
                    className="text-text-secondary hover:text-text-primary transition-colors"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteRole(role.id)}
                    className={`text-error hover:text-error/80 transition-colors ${
                      role.id === '1' || role.id === '4' ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    disabled={role.id === '1' || role.id === '4'}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  , document.body);
};

export default RoleManagementModal;